import { ChatProvider } from "@health/sdk-react";
import type { Brand } from "@health/types";
import { BrandLogo } from "@health/ui/components/brand-logo";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { dispatchAuthChange } from "@/lib/auth/auth-events";
import { AuthHeader } from "../components/auth/AuthHeader";
import { PrototypeChatSurface } from "../components/chat/PrototypeChatSurface";
import { getCategoryBySlug } from "../data/category-display";
import { useBrandTheme } from "../hooks/useBrandTheme";
import { useCategoryTransition } from "../hooks/useCategoryTransition";
import { useSessionReset } from "../hooks/useSessionReset";

export function CategoryChatPage() {
  const { brand, cat } = useParams({ strict: false }) as {
    brand: Brand;
    cat: string;
  };
  const search = useSearch({ strict: false }) as { from?: string };
  const navigate = useNavigate();
  const chatRef = useRef<HTMLDivElement>(null);

  useBrandTheme(brand);
  const handleToolCall = useCategoryTransition(brand);
  const category = getCategoryBySlug(cat);
  const isFromChat = search.from === "chat";
  const serviceUrl = import.meta.env.VITE_COMPANION_SERVICE_URL ?? "";

  // Clear variant cookie on direct entry (not in-session transition) so the
  // server resolves a fresh variant for this category. Keep the session ID
  // so returning users see their recap/history.
  const sessionReady = useSessionReset(!isFromChat, false);

  // Invalid category -> redirect to brand landing
  useEffect(() => {
    if (!category) {
      navigate({ to: "/$brand", params: { brand }, replace: true });
    }
  }, [category, brand, navigate]);

  // Focus management on in-session transition
  useEffect(() => {
    if (isFromChat && chatRef.current) {
      chatRef.current.focus();
    }
  }, [isFromChat]);

  if (!category || !sessionReady) return null;

  return (
    <ChatProvider
      clientOptions={{ baseUrl: serviceUrl }}
      host={{
        hostApiBaseUrl: serviceUrl,
        ...(brand === "hims" || brand === "hers" ? { siteId: brand } : {}),
        onAuthChange: () => dispatchAuthChange(),
      }}
    >
      <div className="flex h-screen flex-col bg-background">
        <header className="flex items-center gap-8 justify-between px-6 max-w-2xl w-full mx-auto">
          <Link to="/$brand" params={{ brand }} className="py-3">
            <BrandLogo brand={brand} />
          </Link>

          <AuthHeader />
        </header>

        <div
          ref={chatRef}
          tabIndex={-1}
          className="flex min-h-0 flex-1 flex-col overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <PrototypeChatSurface
            key={isFromChat ? "session" : `fresh-${cat}`}
            brand={brand}
            category={isFromChat ? undefined : cat}
            surfaceIntent="focused"
            onToolCall={handleToolCall}
          />
        </div>
      </div>
    </ChatProvider>
  );
}
